import { useState, useRef, useCallback } from 'react';
import { graphqlOperation } from '@aws-amplify/api-graphql';
import { generateClient } from '@aws-amplify/api';
import { getPropertySubmissions } from '../../graphql/queries';

const client = generateClient();

// Calculate viewport bounds based on zoom level
function calculateViewportBounds(lat, lon, zoom) {
  // Approximate degrees of latitude/longitude visible based on zoom
  const zoomToDegreesMap = {
    6: 12,
    7: 6,
    8: 3,
    9: 1.5,
    10: 0.75,
    11: 0.375,
    12: 0.1875,
    13: 0.09
  };

  const degrees = zoomToDegreesMap[Math.floor(zoom)] || 1.5;

  return {
    minLat: lat - degrees,
    maxLat: lat + degrees,
    minLon: lon - degrees,
    maxLon: lon + degrees
  };
}

export function usePeoplesPanelData() {
  const [mapData, setMapData] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lon: -0.1278 });
  const [zoomLevel, setZoomLevel] = useState(14);
  const [colorBarRange, setColorBarRange] = useState([0, 1000000]);
  const [isLoading, setIsLoading] = useState(false);
  const isFetchingRef = useRef(false);

  const fetchMapData = useCallback(async (zoom, lat, lon, propertyType = 'All') => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    zoom = Math.max(6, Math.min(13, Math.floor(zoom)));
    setZoomLevel(zoom);

    try {
      // Calculate viewport bounds
      const bounds = calculateViewportBounds(lat, lon, zoom);

      // Query DynamoDB via GraphQL
      const input = {
        minLat: bounds.minLat,
        maxLat: bounds.maxLat,
        minLon: bounds.minLon,
        maxLon: bounds.maxLon,
        propertyType: propertyType
      };

      const response = await client.graphql(
        graphqlOperation(getPropertySubmissions, { input })
      );

      const submissions = response.data?.getPropertySubmissions?.submissions || [];

      if (submissions.length > 0) {
        // Transform data to match expected format
        const transformedData = submissions.map(submission => ({
          latitude: submission.latitude,
          longitude: submission.longitude,
          price: submission.price,
          date: new Date(submission.date).getTime(), // Convert to timestamp
          month_year: new Date(submission.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          type: submission.property_type,
          submission_id: submission.submission_id,
          upvotes: submission.upvotes || 0,
          downvotes: submission.downvotes || 0,
          score: submission.score || 0
        }));

        setMapData(transformedData);

        // Calculate color bar range
        const sortedPrices = transformedData.map(d => d.price).sort((a, b) => a - b);
        setColorBarRange([
          sortedPrices[Math.floor(sortedPrices.length * 0.05)] || 0,
          sortedPrices[Math.floor(sortedPrices.length * 0.95)] || 1000000
        ]);
      } else {
        setMapData([]);
        setColorBarRange([0, 1000000]);
      }
    } catch (error) {
      console.error('Failed to fetch crowdsourced data:', error?.message || 'unknown');
      setMapData([]);
      setColorBarRange([0, 1000000]);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  return {
    isLoading,
    mapData,
    mapCenter,
    zoomLevel,
    colorBarRange,
    setMapCenter,
    fetchMapData,
    setZoomLevel
  };
}
